import { useMemo } from 'react'

import { ApprovalMode } from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import type { ApproverCandidate } from '@ebalance/proto/bffuser/v1/approval_flow_model_pb'
import { useTranslation } from 'react-i18next'

import { TertiaryButton, chakra } from '@enechain/ecloud-designsystem'

import { DsSelect } from '~/components/common/DsSelect'
import { enumSetValueAs } from '~/components/common/EnumSelect'
import { ChevronRightIcon } from '~/pages/AdminApprovalFlow/components/icons'
import {
  type ModeOption,
  SAVE_ERROR_ATTR,
  toDsSelectOptions,
} from '~/pages/AdminApprovalFlow/components/matrixStyles'
import { eclColor } from '~/pages/AdminApprovalFlow/constants/eclColor'
import {
  type CellLocal,
  hasMissingApprover,
  isCellIncomplete,
} from '~/pages/AdminApprovalFlow/models/reducer'

type MatrixModeCellProps = {
  cell: CellLocal | undefined
  modeOptions: ModeOption[]
  onModeChange: (mode: ApprovalMode) => void
  /** 承認段階モーダルを開く (要承認時に呼ばれる)。 */
  onOpenStage: () => void
  /** 保存時点スナップショットから変更されているか (AC-011a)。 */
  changed?: boolean
  /**
   * 承認者候補リスト (= eauth の active member)。 登録済承認者がこの中に無ければ
   * 削除・無効化された「欠落」承認者として扱う (AC-APF-026d)。
   */
  candidates: ApproverCandidate[]
  /**
   * 「自動」セルの中身 (AC-APF-037: 2 行サマリ + 「ルール編集」)。
   * `useAutoBidSection` が返す描画関数の結果をそのまま受け取る。
   * 自動でないセル・自動入札を持たない業務処理では undefined。
   */
  autoBidSummary?: React.ReactNode
  /**
   * 「保存」を押した時に、 このセルが保存を止めている (AC-APF-071)。 承認者未設定は
   * 押す前から select が error の見た目なので、 押した後に増えるのは自動入札ルール
   * 未設定のセルの error の見た目 (8080 `admin/approval-flow.html` の
   * `.apr-mode-select.is-error`)。 セルを直すと親が false に戻す。
   */
  saveError?: boolean
}

/** マトリクスのエリアセル (`<td>`)。モードセレクト + 承認段階バッジ。 */
export const MatrixModeCell = ({
  cell,
  modeOptions,
  onModeChange,
  onOpenStage,
  changed = false,
  candidates,
  autoBidSummary,
  saveError = false,
}: MatrixModeCellProps): React.ReactElement => {
  const { t } = useTranslation()

  const displayMode = cell?.mode ?? ApprovalMode.UNSPECIFIED
  const incomplete = isCellIncomplete(cell)
  const candidateIds = useMemo(
    () => new Set(candidates.map((c) => c.memberId)),
    [candidates],
  )
  // 欠落 (登録済承認者が候補不在 = eauth 上で削除・無効化) は未設定とは別概念だが、
  // 視覚区別は AC-APF-021 の未設定と同じ select invalid 表現に準ずる (Design Note Surfaces)。
  const missingApprover = hasMissingApprover(cell, candidateIds)
  const stageCount = cell?.stages.length ?? 0
  const isApproval = displayMode === ApprovalMode.APPROVAL
  const isAuto = displayMode === ApprovalMode.AUTO

  return (
    // design `admin/approval-flow.html:143` は要承認セルに hover 背景を当てる。
    // 素の <td> では _hover を書けないため chakra.td にする (描画は td のまま)。
    <chakra.td
      {...(saveError ? { [SAVE_ERROR_ATTR]: '' } : {})}
      _hover={isApproval ? { background: eclColor.hoverLow } : undefined}
      onClick={isApproval ? onOpenStage : undefined}
      style={{
        position: 'relative',
        padding: '8px 10px',
        borderBottom: `1px solid ${eclColor.borderLow}`,
        textAlign: 'center',
        verticalAlign: 'top',
        cursor: isApproval ? 'pointer' : 'default',
        // 変更セルは amber 系の背景 + 右上の変更ドットでハイライト (AC-011a)。incomplete(赤) や
        // 「保存」を押した後の error とは配色を分ける (赤い select の地を amber にしない)。
        // 表のセルの左に太い border (inset 3px) は入れない (dev 全体の
        // UI 方針。 2026-09-28 デザイナー判断 #8414 / 10/15 にカードへ拡大)。
        background:
          changed && !incomplete && !saveError
            ? eclColor.surfaceWarningLow
            : undefined,
      }}
    >
      {changed && (
        <chakra.span
          position="absolute"
          top="4px"
          right="4px"
          width="7px"
          height="7px"
          background="Object.warningMid"
          borderRadius="full"
          aria-label={t('page:adminApprovalFlow.cell.changedAria')}
          role="img"
        />
      )}
      {/* design `admin/approval-flow.html:127-129` の .apr-mode-cell-content は
          align-items: stretch (同 :123-126 に「stretch で承認段階ボタン等が select と
          同幅になる」と明記)。 center だとバッジが内容幅のまま浮く。 */}
      <chakra.div
        alignItems="stretch"
        flexDirection="column"
        gap="4px"
        display="flex"
      >
        {/* 単一 select の正は DS `Select` (`~/components/common/DsSelect`)。
            「要承認 + 承認者未設定」は select 自体を error 状態にして attention を
            集める (design `.apr-mode-select.error`)。 DS では `invalid` が
            その役割 (赤枠 + aria-invalid) を担う。
            wrapper の onClick は td の onClick (承認段階モーダルを開く) へ
            伝播させないため (旧 native select の onClick stopPropagation 相当)。 */}
        <chakra.div onClick={(e) => e.stopPropagation()}>
          <DsSelect
            // DS `Select` (chakra-react-select) は枠・区切り・矢印で
            // chrome を約 91px 使う (border 2 + valueContainer paddingX 12×2 +
            // singleValue mx 2×2 + separator/gap + dropdownIndicator 12×2 + icon 20)。
            // 8080 の値は native select (chrome 38px) 前提なのでそのまま移植できず、
            // 表の中では文字領域が足りず選択値が欠けていた (2026-08-24 デザイナー指摘)。
            // 最長表示値「承認なし」 (4 字 × 14px = 56px) + chrome 91px = 147 → 148px。
            minWidth="148px"
            invalid={incomplete || missingApprover || saveError}
            onChange={(raw) => {
              const mode = enumSetValueAs(raw)
              if (mode !== undefined) onModeChange(mode as ApprovalMode)
            }}
            options={toDsSelectOptions(modeOptions)}
            value={displayMode}
          />
        </chakra.div>
        {isApproval && (
          // [design 差異] 8080 `admin/approval-flow.html` は `btn btn-tertiary btn-sm` (承認段階数バッジ。
          // 白地 + 枠 / 高さ 32px) だが、 表のセル内のボタンは全て DS の M サイズ (40px) に揃える
          // (2026-10-30 ユーザー指示「dev > table の TD 内のボタンの height は全て 40px (m サイズ) 準拠」、 #12635。
          // UI 辞書 components/table.md「表のセル内のボタンの高さ (dev、2026-10-30)」)。 DS の TertiaryButton size="md" がそのまま同じ部品なので
          // 手書きの button は使わない (UI 辞書 dictionary/components.md)。
          // 未設定でも赤く塗らない — 未設定のエラー表現は select 側が担う (重複を避ける)。
          <TertiaryButton
            whiteSpace="nowrap"
            aria-label={
              stageCount > 0
                ? t('page:adminApprovalFlow.cell.editAriaLabel', {
                    count: stageCount,
                  })
                : t('page:adminApprovalFlow.cell.unconfiguredAriaLabel')
            }
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              onOpenStage()
            }}
            size="md"
          >
            {stageCount > 0
              ? t('page:adminApprovalFlow.cell.stageCount', {
                  count: stageCount,
                })
              : t('page:adminApprovalFlow.cell.stageUnconfigured')}
            <ChevronRightIcon />
          </TertiaryButton>
        )}
        {/* AC-APF-037: 「自動」セルにはルールの 2 行サマリと「ルール編集」を出す
            (design `.apr-autobid-cellsum`)。要承認セルの承認段階ボタンと同じ位置で、
            モードが排他なので両方が並ぶことはない。
            クリックが td の onClick (承認段階モーダル) へ伝播しないよう止める。 */}
        {isAuto && autoBidSummary !== undefined && (
          <chakra.div onClick={(e) => e.stopPropagation()}>
            {autoBidSummary}
          </chakra.div>
        )}
      </chakra.div>
    </chakra.td>
  )
}
